import { VERSION, COMMIT } from './version'

/**
 * Finding out that the app on screen is not the app that is deployed.
 *
 * An installed web app keeps its JavaScript until something makes it fetch new
 * JavaScript. For a home-screen PWA or a TWA that can be days — the service
 * worker takes a new deploy on the NEXT navigation, and somebody who leaves the
 * app open never makes one. So the honest answer to "does it update
 * automatically" is: eventually, unpredictably, and with no way to tell.
 *
 * The build stamp is compiled into the bundle, which means a running app knows
 * what it IS and cannot know what it SHOULD BE. `version.json` is written beside
 * the bundle at build time and is the one thing the app can ask that is not
 * itself.
 *
 * Extracted from Travel Day, including the decision below, which is the part
 * people get wrong.
 */

export interface DeployedBuild {
  version: string
  commit: string
  builtAt: number
}

export async function deployedBuild(): Promise<DeployedBuild | null> {
  try {
    /*
     * Belt and braces: `no-store` for the HTTP cache, and a query for any
     * intermediary that ignores it. Served as a static asset, so this costs no
     * Worker invocation and still answers when the API is degraded.
     */
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) return null
    const body = (await res.json()) as Partial<DeployedBuild>
    return body.commit ? { version: body.version ?? '', commit: body.commit, builtAt: body.builtAt ?? 0 } : null
  } catch {
    /*
     * Offline, or not deployed yet. Either way there is nothing to claim: an
     * update prompt shown because the network hiccuped is a prompt people learn
     * to ignore, and then the one that matters is ignored too.
     */
    return null
  }
}

/**
 * Whether what is deployed differs from what is running.
 *
 * Compared on the COMMIT, not the version number. Two deploys of the same
 * version are still two different builds, and the one that matters is usually a
 * fix that never got a version bump.
 */
export function isNewer(deployed: DeployedBuild | null): boolean {
  if (!deployed) return false
  // A dev build has no commit worth comparing. Never nag in development.
  if (!COMMIT || COMMIT === 'dev' || COMMIT === 'unknown') return false
  return deployed.commit !== COMMIT
}

/**
 * What they would be moving to. ALWAYS named.
 *
 * This returned an empty string whenever the deployed version matched the
 * running one, which sounds reasonable and produced the exact failure it was
 * meant to prevent: six deploys went out under one version number, so the bar
 * appeared saying "a newer version is ready" and named nothing at all. An
 * update prompt that will not say what it is offering is one people learn to
 * dismiss.
 *
 * Same number, different build, is worth saying out loud — it is usually a fix,
 * and it is always a reason the app on screen is not the app that is deployed.
 */
export function deployedLabel(deployed: DeployedBuild): string {
  if (!deployed.version) return ''
  return deployed.version === VERSION ? `v${deployed.version}, new build` : `v${VERSION} → v${deployed.version}`
}
